import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Clock, Users, ExternalLink, Loader2, AlertCircle, Search, CloudUpload, X, FolderOpen } from 'lucide-react';
import { getSearchSessions, type SearchSession, type SearchSessionsResponse } from '../../services/api';

const MATCHING_BASE = 'https://matching.turing.com/search-v3';

const openSessionUrl = (id: number) =>
  `${MATCHING_BASE}?session=${id}&restore=true&searched=true`;

// Shared page size for the tab (first page) and the "View all" modal, so the
// modal can reuse the already-loaded first page and continue from page 2.
const PAGE_SIZE = 5;

// Module-level cache of the first page → stale-while-revalidate across tab
// switches: show the previous response instantly, refresh in the background.
let sessionsCache: SearchSessionsResponse | null = null;

// ─── Formatting helpers ──────────────────────────────────────────────────────
function formatSessionDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month} ${day} ${hours}:${minutes}${ampm}`;
}

function truncate(text: string, max = 300): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '...';
}

function pluralize(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function queriesLabel(n: number): string {
  return `${n} search quer${n === 1 ? 'y' : 'ies'}`;
}

// Required skills: handles string[] or object[] ({ skill|name, years }), with an
// optional skillYears map from the filters payload.
function formatRequiredSkills(filters: Record<string, any> | undefined): string {
  if (!filters) return 'No required skills';
  const skills = filters.requiredSkills;
  const skillYears: Record<string, any> = filters.skillYears || {};
  if (!Array.isArray(skills) || skills.length === 0) return 'No required skills';
  const parts = skills
    .map((s: any) => {
      if (s == null) return null;
      if (typeof s === 'string') {
        const yr = skillYears[s];
        return yr ? `${s} (${yr}yr)` : s;
      }
      const name = s.skill || s.name || s.label;
      if (!name) return null;
      const yr = s.years ?? s.year ?? skillYears[name];
      return yr ? `${name} (${yr}yr)` : name;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No required skills';
}

function formatLocation(
  filters: Record<string, any> | undefined,
  decomposition: Record<string, any> | undefined,
): string {
  const collect = (v: any): string[] =>
    Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()) : [];
  const locs = [
    ...collect(filters?.selectedCountries),
    ...collect(filters?.selectedRegions),
    ...collect(decomposition?.country),
    ...collect(decomposition?.city),
  ];
  const unique = Array.from(new Set(locs));
  return unique.length ? unique.join(', ') : 'Global';
}

function formatContextTags(filters: Record<string, any> | undefined): string {
  const collect = (v: any): string[] =>
    Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()) : [];
  const tags = [...collect(filters?.contextTags), ...collect(filters?.domainTags)];
  const unique = Array.from(new Set(tags));
  return unique.length ? unique.join(', ') : 'No context tags captured';
}

// ─── Filter grid (shared by latest card) ─────────────────────────────────────
function FilterGrid({ session }: { session: SearchSession }) {
  const filters = session.latestQuery?.filters;
  const decomposition = session.latestQuery?.decomposition;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div>
        <div className="mb-1 text-sm font-semibold text-gray-500 dark:text-gray-400">Required Skills</div>
        <div className="text-sm leading-6 text-gray-900 dark:text-gray-100">
          {formatRequiredSkills(filters)}
        </div>
      </div>
      <div>
        <div className="mb-1 text-sm font-semibold text-gray-500 dark:text-gray-400">Location</div>
        <div className="text-sm text-gray-900 dark:text-gray-100">{formatLocation(filters, decomposition)}</div>
      </div>
      <div>
        <div className="mb-1 text-sm font-semibold text-gray-500 dark:text-gray-400">Search Context Tags</div>
        <div className="text-sm text-gray-900 dark:text-gray-100">{formatContextTags(filters)}</div>
      </div>
    </div>
  );
}

// ─── Latest session card ─────────────────────────────────────────────────────
function LatestSessionCard({ session }: { session: SearchSession }) {
  const queryText = session.latestQuery?.queryText?.trim();
  return (
    <a
      href={openSessionUrl(session.id)}
      target="_blank"
      rel="noreferrer"
      className="block rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md"
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-950 dark:text-gray-50">{session.title}</h2>
          </div>
          {session.jobId != null && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Job #{session.jobId}</p>
          )}
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <Clock className="h-3.5 w-3.5" />
          {formatSessionDate(session.updatedAt)}
        </span>
      </div>

      <div className="space-y-3">
        {(session.queryCount > 0 || queryText) && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-50">
                {queriesLabel(session.queryCount)}
              </h3>
            </div>
            {queryText && (
              <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">{truncate(queryText)}</p>
            )}
          </div>
        )}

        {session.filterCount > 0 && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-50">
                {pluralize(session.filterCount, 'search filter')}
              </h3>
            </div>
            <FilterGrid session={session} />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <Users className="h-4 w-4 text-gray-400" />
          {pluralize(session.cartCount, 'talent')} in Talent Cart
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <span className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm">
            <ExternalLink className="h-4 w-4" />
            Open Session
          </span>
        </div>
      </div>
    </a>
  );
}

// ─── Previous session card (compact grid item) ───────────────────────────────
function PreviousSessionCard({ session }: { session: SearchSession }) {
  const queryText = session.latestQuery?.queryText?.trim();
  return (
    <a
      href={openSessionUrl(session.id)}
      target="_blank"
      rel="noreferrer"
      className="block min-h-[118px] rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-md"
    >
      <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-400">
        <Clock className="h-3.5 w-3.5" />
        {formatSessionDate(session.updatedAt)}
      </div>
      <div className="truncate text-sm font-semibold text-gray-950 dark:text-gray-50">{session.title}</div>
      {queryText && (
        <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">{queryText}</div>
      )}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {queriesLabel(session.queryCount)}
        <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
        {pluralize(session.cartCount, 'talent')} in Talent Cart
      </div>
      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
        Open Session
        <ExternalLink className="h-3 w-3" />
      </span>
    </a>
  );
}

// ─── Modal session card (full detail, used in "View all" sheet) ──────────────
function ModalSessionCard({ session }: { session: SearchSession }) {
  const queryText = session.latestQuery?.queryText?.trim();
  const filters = session.latestQuery?.filters;
  const decomposition = session.latestQuery?.decomposition;
  return (
    <div className="w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-950 dark:text-gray-50">{session.title}</h3>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] text-gray-500 dark:text-gray-400">
          <Clock className="h-3 w-3" />
          {formatSessionDate(session.updatedAt)}
        </span>
      </div>

      <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3">
        <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">{queriesLabel(session.queryCount)}</h4>
        {queryText && (
          <p className="mt-2 text-xs leading-5 text-gray-700 dark:text-gray-300">{truncate(queryText, 400)}</p>
        )}
      </div>

      <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3">
        <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">{pluralize(session.filterCount, 'search filter')}</h4>
        <div className="mt-2 grid gap-3 text-xs sm:grid-cols-3">
          <div className="min-w-0">
            <p className="font-semibold text-gray-500 dark:text-gray-400">Required Skills</p>
            <p className="mt-1 leading-5 text-gray-900 dark:text-gray-100">{formatRequiredSkills(filters)}</p>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-500 dark:text-gray-400">Location</p>
            <p className="mt-1 leading-5 text-gray-900 dark:text-gray-100">{formatLocation(filters, decomposition)}</p>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-500 dark:text-gray-400">Search Context Tags</p>
            <p className="mt-1 leading-5 text-gray-900 dark:text-gray-100">{formatContextTags(filters)}</p>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
          <Users className="h-4 w-4 text-gray-400" />
          {pluralize(session.cartCount, 'talent')} in Talent Cart
        </div>
        <a
          href={openSessionUrl(session.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open Session
        </a>
      </div>
    </div>
  );
}

// ─── "View all" modal — paginated, infinite scroll ───────────────────────────
function AllSessionsModal({
  onClose,
  onUploadCsv,
  initial,
}: {
  onClose: () => void;
  onUploadCsv: () => void;
  initial?: SearchSessionsResponse | null;
}) {
  // Seed from the first page the tab already loaded; only fetch page 2+ on scroll.
  const [items, setItems] = useState<SearchSession[]>(initial?.items ?? []);
  const [page, setPage] = useState(initial ? 1 : 0); // last successfully loaded page (0 = none yet)
  const [total, setTotal] = useState(initial?.total ?? 0);
  const [totalPages, setTotalPages] = useState(initial?.totalPages ?? 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entered, setEntered] = useState(false); // drives slide-in / slide-out
  const loadingRef = useRef(false);

  // Slide in on mount
  useEffect(() => { setEntered(true); }, []);

  // Animate out, then unmount
  const handleClose = useCallback(() => {
    setEntered(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    const next = page + 1;
    if (page > 0 && next > totalPages) return; // all loaded
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await getSearchSessions(next, PAGE_SIZE);
      setItems((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        return [...prev, ...(res.items || []).filter((s) => !seen.has(s.id))];
      });
      setPage(next);
      setTotal(res.total ?? 0);
      setTotalPages(res.totalPages ?? 1);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not load more sessions.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [page, totalPages]);

  // Only fetch on mount when there's no seeded first page.
  useEffect(() => {
    if (!initial) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 240;
    if (nearBottom && !loadingRef.current && page < totalPages) {
      loadMore();
    }
  };

  const hasLoadedOnce = page > 0;

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-y-0 right-0 left-64 z-50 flex flex-col bg-neutral-50 dark:bg-gray-950 shadow-2xl transition-transform duration-300 ease-in-out ${
        entered ? 'translate-x-0' : 'translate-x-full'
      }`}
      role="dialog"
      aria-modal="true"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-4">
        <div className="mx-auto flex w-full max-w-4xl items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <FolderOpen className="h-4 w-4 text-blue-600" />
              Search Sessions
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Restore saved Talent Search workspaces.</p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 shadow-sm transition-colors hover:text-gray-800 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Scroll body — infinite scroll */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4" onScroll={handleScroll}>
        <div className="mx-auto w-full max-w-4xl space-y-4">
          {items.map((s) => (
            <ModalSessionCard key={s.id} session={s} />
          ))}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-center">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              <button onClick={loadMore} className="mt-2 text-sm font-medium text-red-600 dark:text-red-400 underline">
                Retry
              </button>
            </div>
          )}

          {hasLoadedOnce && !loading && !error && items.length === 0 && (
            <div className="py-12 text-center">
              <Search className="mx-auto mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No search sessions yet</p>
              <button
                onClick={() => { handleClose(); onUploadCsv(); }}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                <CloudUpload className="h-4 w-4" />
                Upload CSV instead
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {hasLoadedOnce && items.length > 0 && (
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-3">
          <div className="mx-auto flex w-full max-w-4xl items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Showing {items.length} of {total} session{total !== 1 ? 's' : ''}</span>
            {page >= totalPages ? <span>All loaded</span> : <span>Scroll for more</span>}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ─── Tab ─────────────────────────────────────────────────────────────────────
export function SearchSessionsTab({ onUploadCsv }: { onUploadCsv: () => void }) {
  const [resp, setResp] = useState<SearchSessionsResponse | null>(sessionsCache);
  const [sessions, setSessions] = useState<SearchSession[]>(sessionsCache?.items ?? []);
  const [loading, setLoading] = useState(!sessionsCache);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = () => {
    const cached = sessionsCache;
    if (cached) {
      // Show cached (stale) immediately, refresh in the background.
      setResp(cached);
      setSessions(cached.items || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError('');
    getSearchSessions(1, PAGE_SIZE)
      .then((res) => {
        sessionsCache = res;
        setResp(res);
        setSessions(res.items || []);
        setError('');
      })
      .catch((err: any) => {
        // Keep showing stale data if a background refresh fails.
        if (!cached) {
          setError(err?.response?.data?.message || 'Could not load search sessions. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading search sessions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Couldn’t load your search sessions</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
          {error} You can retry, or add candidates by uploading a CSV instead.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={onUploadCsv}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <CloudUpload className="h-4 w-4" />
            Upload CSV instead
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 text-center">
        <Search className="mx-auto mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No search sessions yet</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Start a Talent Search session, or add candidates by uploading a CSV.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={onUploadCsv}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <CloudUpload className="h-4 w-4" />
            Upload CSV instead
          </button>
          <a
            href={MATCHING_BASE}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ExternalLink className="h-4 w-4" />
            Open Talent Search
          </a>
        </div>
      </div>
    );
  }

  const [latest, ...previous] = sessions;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Latest Search Session</h2>
        <LatestSessionCard session={latest} />
      </div>

      {previous.length > 0 && (
        <div className="space-y-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Previous Search Sessions</h2>
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 transition-colors hover:text-blue-700"
            >
              View all
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {previous.slice(0, 3).map((s) => (
              <PreviousSessionCard key={s.id} session={s} />
            ))}
          </div>
        </div>
      )}

      {showAll && (
        <AllSessionsModal onClose={() => setShowAll(false)} onUploadCsv={onUploadCsv} initial={resp} />
      )}
    </div>
  );
}
